// Modelo del cobro en tienda en tres pasos: cuánto, cómo y confirmar; el monto sale de las opciones.
import type { CreateSellerPaymentRequest, SellerPaymentOptions } from '@/api/services/loan-credit';
import { CHARGE_DETAIL_ERROR } from './collectionsFormat';

/** Qué se cobra: su cuota, adelantar N cuotas, abono con monto o liquidar. */
export type ChargeKind =
    | { type: 'NEXT_INSTALLMENT' }
    | { type: 'ADVANCE'; installmentCount: number }
    | { type: 'PREPAYMENT'; amountCents: number }
    | { type: 'SETTLEMENT' };

/** Cómo pagó el cliente y su dato. */
export type ChargePayment =
    | { method: 'CASH' }
    | { method: 'CARD'; cardLast4: string; authCode: string }
    | { method: 'WIRE'; speiTrackingKey: string };

export interface ChargeDraft {
    kind: ChargeKind;
    payment: ChargePayment;
}

export type ChargeStep = 'amount' | 'method' | 'confirm';

const CARD_LAST4 = /^\d{4}$/;
const AUTH_CODE = /^[A-Za-z0-9]{1,12}$/;
const SPEI_TRACKING_KEY_MAX = 30;

/** Monto del tipo elegido según las opciones del servidor. */
export function chargeAmountOf(options: SellerPaymentOptions, kind: ChargeKind): number {
    switch (kind.type) {
        case 'NEXT_INSTALLMENT':
            return options.nextInstallment.amountCents ?? 0;
        case 'ADVANCE':
            return (options.advance.installmentAmountsCents ?? [])
                .slice(0, kind.installmentCount)
                .reduce((total, cents) => total + cents, 0);
        case 'PREPAYMENT':
            return kind.amountCents;
        case 'SETTLEMENT':
            return options.settlement.amountCents ?? 0;
    }
}

/** El dato que falta o está mal en la forma de pago; null si está completo. */
export function chargeDetailErrorOf(payment: ChargePayment): string | null {
    if (payment.method === 'CARD') {
        if (!CARD_LAST4.test(payment.cardLast4)) return CHARGE_DETAIL_ERROR.cardLast4;
        if (!AUTH_CODE.test(payment.authCode)) return CHARGE_DETAIL_ERROR.authCode;
    }
    if (payment.method === 'WIRE') {
        const key = payment.speiTrackingKey.trim();
        if (key.length === 0) return CHARGE_DETAIL_ERROR.speiMissing;
        if (key.length > SPEI_TRACKING_KEY_MAX) return CHARGE_DETAIL_ERROR.speiTooLong;
    }
    return null;
}

function detailOf(payment: ChargePayment): CreateSellerPaymentRequest['detail'] {
    if (payment.method === 'CARD')
        return { cardLast4: payment.cardLast4, authCode: payment.authCode };
    if (payment.method === 'WIRE') return { speiTrackingKey: payment.speiTrackingKey.trim() };
    return undefined;
}

/** El cuerpo del POST: el monto de las opciones, la forma con SOLO su dato y la llave del intento. */
export function chargeRequestOf(
    options: SellerPaymentOptions,
    draft: ChargeDraft,
    idempotencyKey: string,
): CreateSellerPaymentRequest {
    const { payment, kind } = draft;
    const detail = detailOf(payment);
    return {
        type: kind.type,
        expectedAmountCents: chargeAmountOf(options, kind),
        ...(kind.type === 'ADVANCE' ? { installmentCount: kind.installmentCount } : {}),
        method: payment.method,
        ...(detail ? { detail } : {}),
        idempotencyKey,
    } as CreateSellerPaymentRequest;
}

/** Huella de lo que eligió el vendedor: si cambia, el intento lleva llave nueva; recargar opciones no. */
export function chargeFingerprintOf({ kind, payment }: ChargeDraft): string {
    const normalized =
        payment.method === 'WIRE'
            ? { ...payment, speiTrackingKey: payment.speiTrackingKey.trim() }
            : payment;
    return JSON.stringify({ kind, payment: normalized });
}
